import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTrackData } from '../data/useTrackData';
import { useOverlay } from '../auth/overlay';
import { useRouteSource } from '../data-source/provider';
import { useRequest } from '../../api/useRequest';
import { api } from '../../api/client';
import type { ContourMeta, Crop, Farm, Massif } from '../data/types';
import { scopeQuery, summarize, territoryRows, validDate, weeklyFlow, type Journal, type Scope, type Summary } from './model';
import { buildSituations, type Situation, type SituationLens } from './situations';
import {
  seasonQuery,
  toJournalShell,
  toSummary,
  toTerritoryRows,
  toWeekly,
  type TerritoryRow,
  type WeeklyPoint,
} from '../data-source/api/season';
import { situationQuery, toSituationsView, type SituationsView } from '../data-source/api/situations';
import { readAnalysisContext, saveAnalysisContext } from './context';
import { useTrack } from '../data/TrackContext';
import { usePersona } from '../auth/RoleContext';
import { useAnalysisContext } from '../../analysis/context';
import { defineMessages } from '../../i18n/define';
import { tr } from '../../i18n/runtime';

const msg = defineMessages({
  farmerFarmMissing: { id: 'board.analysis.farmerFarmMissing', defaultMessage: 'Ваше хозяйство не найдено в этом наборе данных. Переключите набор или войдите заново.', description: 'Ошибка вместо экрана (фермер): его хозяйства нет в выбранном демо-наборе данных' },
  farmMissing: { id: 'board.analysis.farmMissing', defaultMessage: 'Хозяйство не найдено в выбранной территории. Сбросьте фильтр хозяйства.', description: 'Ошибка вместо экрана (менеджер): выбранного в фильтре хозяйства нет в выбранной территории' },
  massifFallback: { id: 'board.analysis.massifFallback', defaultMessage: 'Массив №{id}', description: 'Имя массива, когда у него нет названия в данных; {id} — код массива' },
  farmFallback: { id: 'board.analysis.farmFallback', defaultMessage: 'Хозяйство №{id}', description: 'Имя хозяйства, когда у него нет названия в данных; {id} — код хозяйства' },
});

/**
 * Один хук — два источника, и ВЫБОР источника делает не экран.
 *
 * Маршрут объявляет источник в `data-source/routes.ts`; здесь это объявление
 * читается и определяет, откуда приедут те же самые поля. На фикстурах сводка
 * считается в браузере (`summarize` по `analysis.json`), на каноне её СЧИТАЕТ
 * СЕРВЕР: на районных объёмах разворачивать сорок тысяч обязательств в
 * браузере нельзя, и именно поэтому переезд вообще затеян. Экран при этом не
 * знает, что изменилось: форма полей одна.
 *
 * Отсюда правило, которое держит переезд честным: на каноническом маршруте
 * `summarize` и `buildSituations` НЕ вызываются вовсе — не «вызываются и
 * игнорируются», а не вызываются. Фикстурные наборы на таком маршруте тоже не
 * читаются (`useTrackData(null)`), иначе экран тянул бы мегабайты, которые
 * никому не покажет.
 */
/** Сколько строк очереди приезжает за один запрос. «Ещё» просит следующие. */
const SITUATION_PAGE = 60;

export function useAnalysis(fixed?: { cycleId: string; workId?: string }) {
  const route = useRouteSource();
  const onApi = route?.source === 'api';
  const { track } = useTrack();
  const journal = useTrackData<Journal>(onApi ? null : 'analysis.json');
  const contours = useTrackData<ContourMeta[]>(onApi ? null : 'contours.meta.json');
  const fixtureFarms = useTrackData<Farm[]>(onApi ? null : 'farms.json');
  const fixtureMassifs = useTrackData<Massif[]>(onApi ? null : 'massifs.json');
  const crops = useTrackData<Crop[]>(onApi ? null : 'crops.json');
  const overlay = useOverlay();
  const { persona } = usePersona();
  /**
   * Уровень фермера заканчивается его хозяйством (docs/rules/screen-levels.md).
   * Проверка маршрута смотрит только на форму адреса, поэтому границу данных
   * держит здесь СРЕЗ: хозяйство берётся из персоны, а `farm`/`massif` из
   * адреса и сессии игнорируются — иначе карточка контура или работы показала
   * бы фермеру знаменатель района. На каноне ту же границу применяет ещё и
   * сервер: спрятанный фильтр защитой не считается.
   */
  const farmerFarmId = persona?.role === 'farmer' ? persona.farmId ?? null : null;
  const [params, setParams] = useSearchParams();
  const context = ['cycle', 'date', 'massif', 'farm'].some(key => params.has(key)) || fixed ? params : readAnalysisContext(track);

  // Циклы называют срез, поэтому приезжают ПЕРВЫМИ и отдельным запросом: без
  // списка циклов нечем проверить, существует ли цикл, стоящий в адресе.
  // Демо — флаг среза (галка «Показывать демо-данные»), а не константа
  // экрана: тот же флаг получают карточки хозяйства и массива, и два экрана
  // одного среза не расходятся в том, существует ли синтетика.
  const { includeDemo, asOf: sharedAsOf, setAsOf: setSharedAsOf } = useAnalysisContext();
  const cycles = useRequest(
    () => (onApi ? api.seasonCycles(includeDemo ? { include_demo: true } : {}) : Promise.resolve(null)),
    [onApi, includeDemo],
  );
  const canonJournal = useMemo(
    () => (cycles.data ? toJournalShell(cycles.data) : null),
    [cycles.data],
  );

  const data = onApi ? canonJournal : journal.data;
  // Осенью идут два цикла сразу (сбор хлопка и сев пшеницы): по умолчанию
  // открывается хлопок, пшеница — переключателем культуры.
  const defaultCycle = (data?.cycles.find(c => c.status === 'active' && c.crop === 'cotton') ?? data?.cycles.find(c => c.status === 'active'))?.id ?? (onApi ? '' : 'cycle-cotton-2026');
  const requestedCycle = fixed?.cycleId ?? context.get('cycle');
  const cycleId = data?.cycles.some(c => c.id === requestedCycle) ? requestedCycle! : defaultCycle;
  /**
   * Дата среза на каноне — ОДНА на продукт (agroai-0yt.2): её задаёт полоса
   * контекста («Дата состояния», параметр `as_of`), и ту же дату читают
   * «Реестр района» и карточки. Своя дата экрана (`date` в адресе и в памяти
   * сессии) на каноне больше не читается: из-за неё главная показывала две
   * даты сразу, а сводка к совещанию — третью, из памяти. Пустая дата полосы
   * означает «как знаем сейчас» — горизонт данных сервера.
   */
  const requestedDate = onApi ? sharedAsOf : context.get('date');
  const asOf = requestedDate && validDate(requestedDate) && requestedDate >= '2024-10-01' && requestedDate <= (data?.as_of ?? '') ? requestedDate : data?.as_of ?? '2026-08-26';
  const requestedMassif = farmerFarmId ? null : context.get('massif');
  // На каноне массив проверяется формой идентификатора, а принадлежность
  // области чтения — сервером: список массивов приезжает той же картиной, что
  // и сводка, и требовать его ДО запроса значило бы сделать лишний круг.
  const massifId = onApi
    ? (requestedMassif && Number.isInteger(Number(requestedMassif)) ? requestedMassif : undefined)
    : (fixtureMassifs.data?.some(m => m.id === requestedMassif) ? requestedMassif! : undefined);
  const farmId = farmerFarmId ?? context.get('farm') ?? undefined;
  const scopeBroken = !onApi && !!farmId && !!fixtureFarms.data && !!contours.data && (!fixtureFarms.data.some(f => f.id === farmId) || (!!massifId && !contours.data.some(c => c.farm_id === farmId && c.massif_id === massifId)));
  const scopeError = !scopeBroken ? null : farmerFarmId ? tr(msg.farmerFarmMissing) : tr(msg.farmMissing);
  const workId = fixed?.workId;
  const scope: Scope = useMemo(() => ({ cycleId, asOf, massifId, farmId, workId, includeDemo }), [cycleId, asOf, massifId, farmId, workId, includeDemo]);

  // Картина сезона и очередь ситуаций — два запроса одного среза. Разделены,
  // потому что отвечают на разные вопросы: «сколько всего» и «где разбираться».
  const ready = onApi && !!cycleId;
  const picture = useRequest(
    () =>
      ready
        ? api.seasonPicture(seasonQuery(scope, { summary: true, weeks: 9 }))
        : Promise.resolve(null),
    [ready, scope],
  );
  /**
   * Линза очереди — параметр СЕРВЕРА, а не фильтр по скачанной странице.
   *
   * Сервер по линзе сужает выдачу, и множества линз не вложены друг в друга:
   * «все сигналы» отбираются по порогу существенности, а «отчёт и вода» или
   * «проверка» — по факту признака. Отфильтровать страницу `lens=all` в
   * браузере значило бы показать под счётчиком линзы другой список: часть её
   * строк в скачанной странице физически отсутствует.
   */
  const [lens, setLens] = useState<SituationLens>('all');
  const [pageSize, setPageSize] = useState(SITUATION_PAGE);
  // Смена линзы или среза начинает страницу заново: «Ещё» относится к той
  // очереди, которую человек сейчас видит.
  useEffect(() => { setPageSize(SITUATION_PAGE); }, [lens, scope]);
  const situationList = useRequest(
    () => (ready ? api.situations(situationQuery(scope, { lens, limit: pageSize })) : Promise.resolve(null)),
    [ready, scope, lens, pageSize],
  );

  const canonSummary = useMemo(
    () => (picture.data ? toSummary(picture.data, scope) : null),
    [picture.data, scope],
  );
  const fixtureSummary = useMemo(
    () => (!onApi && journal.data && contours.data ? summarize(journal.data, contours.data, scope, overlay) : null),
    [onApi, journal.data, contours.data, scope, overlay],
  );
  const summary: Summary | null = onApi ? canonSummary : fixtureSummary;

  /**
   * Массивы и хозяйства на каноне берутся из той же картины, что и сводка.
   *
   * Отдельные реестры (`/massifs`, `/actors`) ответили бы про весь район, а
   * выбор среза относится к ЭТОМУ циклу: массив без работ цикла в списке
   * территории — приглашение выбрать пустой знаменатель.
   */
  const canonMassifs = useMemo<Massif[]>(
    () =>
      (picture.data?.summary?.by_massif ?? []).map(row => ({
        id: String(row.id),
        name: row.name ?? tr(msg.massifFallback, { id: String(row.id) }),
        area_ha: (row.area.total_microha ?? 0) / 1_000_000,
        contours: row.contours.total,
        farms: 0,
        center: [0, 0] as [number, number],
      })),
    [picture.data],
  );
  const canonFarms = useMemo<Farm[]>(
    () =>
      (picture.data?.summary?.by_farm ?? []).map(row => ({
        id: String(row.id),
        name: row.name ?? tr(msg.farmFallback, { id: String(row.id) }),
        massif_ids: [],
        direction: '',
        area_ha: (row.area.total_microha ?? 0) / 1_000_000,
        contours: row.contours.total,
        head: '',
      })),
    [picture.data],
  );

  const canonSituations = useMemo<SituationsView | null>(
    () => (situationList.data ? toSituationsView(situationList.data) : null),
    [situationList.data],
  );
  const fixtureSituations = useMemo<Situation[]>(
    () => (!onApi && fixtureSummary && journal.data ? buildSituations(fixtureSummary, journal.data, contours.data ?? [], fixtureFarms.data ?? []) : []),
    [onApi, fixtureSummary, journal.data, contours.data, fixtureFarms.data],
  );
  const situations: Situation[] = onApi ? canonSituations?.items ?? [] : fixtureSituations;

  const territory = useMemo<TerritoryRow[]>(
    () =>
      onApi
        ? picture.data ? toTerritoryRows(picture.data) : []
        : summary ? territoryRows(summary, contours.data ?? [], fixtureMassifs.data ?? [], fixtureFarms.data ?? []) : [],
    [onApi, picture.data, summary, contours.data, fixtureMassifs.data, fixtureFarms.data],
  );
  /**
   * Недельный ряд. На фикстурах он НАКОПЛЕННЫЙ (девять точек «на дату»), на
   * каноне — события КАЖДОЙ недели. Накопление с начала сезона по окну из
   * девяти недель не восстанавливается, поэтому режим «Накоплено» на каноне
   * закрыт, а не досчитан из того, что под рукой.
   */
  const weekly = useMemo<WeeklyPoint[]>(
    () => (onApi ? (picture.data ? toWeekly(picture.data) : []) : summary ? weeklyFlow(summary, 9) : []),
    [onApi, picture.data, summary],
  );

  // Срез фермера не кладётся в общий контекст сессии: он навязан персоной, а не
  // выбран человеком, и не должен становиться фильтром для следующей роли.
  //
  // Ключ памяти — трек ПРИЛОЖЕНИЯ (`useTrack`), тот же, которым срез читается
  // выше и которым его читают ссылки сайдбара (`analysisNavigationQuery`).
  // Раньше запись шла под именем набора (`data.track`, на каноне — «КАНОН»),
  // а чтение — под треком: срез, выбранный на «/», не доезжал до сводки для
  // совещания и обучения, и те открывались на старой дате из другой памяти.
  useEffect(() => { if (data && !scopeError && !farmerFarmId) saveAnalysisContext(track, scope); }, [data, track, scope, scopeError, farmerFarmId]);
  const setScope = (key: string, value: string) => { if (onApi && key === 'date') { setSharedAsOf(value && value !== data?.as_of ? value : null); return; } const next = new URLSearchParams(scopeQuery(scope)); if (value) next.set(key, value); else next.delete(key); if (key === 'massif') next.delete('farm'); if (onApi && sharedAsOf) next.set('as_of', sharedAsOf); setParams(next, { replace: true }); };
  // На каноне общая дата едет в адресе как `as_of` — тем же параметром, что
  // ставит полоса контекста; переход по ссылке экрана её не теряет.
  const withShared = (q: string) => onApi && sharedAsOf ? `${q}&as_of=${sharedAsOf}` : q;
  const restoreScope = (next: Scope) => { const p = new URLSearchParams(scopeQuery({ ...next, includeDemo })); if (onApi && sharedAsOf) p.set('as_of', sharedAsOf); setParams(p, { replace: true }); };
  const query = withShared(scopeQuery(scope));

  const canonError = onApi ? [cycles, picture, situationList].find(r => r.error)?.error?.message : undefined;
  const canonLoading = onApi && (cycles.state === 'loading' || picture.state === 'loading' || situationList.state === 'loading');
  // Фикстурные наборы на каноническом маршруте не читаются вовсе, поэтому их
  // «файл не выбран» — не отсутствие данных экрана, а отсутствие вопроса.
  const fixtureSets = onApi ? [] : [journal, contours, fixtureFarms, fixtureMassifs];

  return {
    data,
    contours: onApi ? [] : contours.data ?? [],
    farms: onApi ? canonFarms : fixtureFarms.data ?? [],
    massifs: onApi ? canonMassifs : fixtureMassifs.data ?? [],
    crops: crops.data ?? [],
    summary,
    /** Очередь ситуаций — уже посчитанная. Экран её НЕ строит. */
    situations,
    /** Оговорки и пороги очереди: на каноне — словарные статьи сервера. */
    situationsView: onApi ? canonSituations : null,
    /** Перечитать очередь после записи диспозиции: строка меняется на сервере. */
    reloadSituations: situationList.reload,
    /** Выбранная линза очереди и её смена: на каноне это запрос к серверу. */
    situationLens: lens,
    setSituationLens: setLens,
    /** Дозапросить следующую часть очереди той же линзы. */
    moreSituations: () => setPageSize(n => n + SITUATION_PAGE),
    territory,
    weekly,
    /** Накопленный режим графика имеет смысл только на накопленном ряде. */
    weeklyCumulative: !onApi,
    /** Откуда пришли числа этого экрана. Экран печатает это словами. */
    sourceKind: onApi ? ('api' as const) : ('fixtures' as const),
    scope,
    setScope,
    restoreScope,
    query,
    overlay,
    scopeError,
    cycle: data?.cycles.find(c => c.id === cycleId),
    loading: canonLoading || fixtureSets.some(d => d.state === 'loading'),
    /** Ошибка СВЯЗИ с сервером (а не «данных нет»): экран предлагает повтор. */
    failedRequest: onApi && !!canonError,
    /** Повторить запросы среза после ошибки связи (agroai-6al.35). */
    reload: () => { cycles.reload(); picture.reload(); situationList.reload(); },
    error: scopeError ?? canonError ?? fixtureSets.find(d => d.state === 'missing')?.reason,
  };
}
export type Analysis = ReturnType<typeof useAnalysis>;
